import { useCallback, useEffect, useRef, useState } from "react";
import { createVeriffFrame, MESSAGES } from "@veriff/incontext-sdk";
import { ShieldCheck, Loader2, AlertTriangle, Clock, X } from "lucide-react";
import { useTranslation } from "react-i18next";
import api from "@/lib/api";

// Runs the Veriff identity flow: create a session, open the Veriff InContext
// frame, then poll our backend until a decision is reached.
export default function VeriffVerification({ createSession, pollToken, onVerified, onClose }) {
  const { t } = useTranslation();
  const [phase, setPhase] = useState("starting"); // starting | active | checking | review | declined | error
  const [detail, setDetail] = useState("");
  const frameRef = useRef(null);
  const pollRef = useRef(null);
  const sessionRef = useRef(null);

  const stopPolling = () => { if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; } };

  const poll = useCallback((sessionId) => {
    stopPolling();
    let tries = 0;
    pollRef.current = setInterval(async () => {
      tries += 1;
      try {
        const r = await api.get(`/veriff/session/${sessionId}/status`, { params: pollToken ? { token: pollToken } : {} });
        const s = r.data.status;
        if (s === "approved") { stopPolling(); setPhase("approved"); setTimeout(() => onVerified?.(), 900); return; }
        if (["declined", "expired", "abandoned", "rules_failed"].includes(s)) {
          stopPolling();
          setPhase("declined");
          const rule = (r.data.rules || [])[0];
          setDetail(rule?.detail || "");
          return;
        }
        if (s === "review") { stopPolling(); setPhase("review"); return; }
      } catch { /* transient — keep polling */ }
      if (tries >= 40) { stopPolling(); setPhase("review"); }
    }, 3000);
  }, [pollToken, onVerified]);

  const start = useCallback(async () => {
    setPhase("starting");
    setDetail("");
    try {
      const s = await createSession();
      if (s.already_verified) { onVerified?.(); return; }
      sessionRef.current = s;
      setPhase("active");
      frameRef.current = createVeriffFrame({
        url: s.url,
        onEvent: (msg) => {
          if (msg === MESSAGES.FINISHED) { setPhase("checking"); poll(s.session_id); }
          if (msg === MESSAGES.CANCELED) { stopPolling(); onClose?.(); }
        },
      });
    } catch (e) {
      setPhase("error");
      const d = e.response?.data?.detail;
      setDetail(typeof d === "string" ? d : d?.message || "");
    }
  }, [createSession, onClose, poll]);

  useEffect(() => {
    start();
    return () => { stopPolling(); try { frameRef.current?.close(); } catch { /* already closed */ } };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const body = {
    starting: { icon: <Loader2 size={22} className="animate-spin text-blue-600" />, text: t("veriff.starting") },
    active: { icon: <ShieldCheck size={22} className="text-blue-600" />, text: t("veriff.intro") },
    checking: { icon: <Loader2 size={22} className="animate-spin text-blue-600" />, text: t("veriff.checking") },
    approved: { icon: <ShieldCheck size={22} className="text-green-600" />, text: t("veriff.approved") },
    review: { icon: <Clock size={22} className="text-amber-500" />, text: t("veriff.underReview") },
    declined: { icon: <AlertTriangle size={22} className="text-red-500" />, text: detail || t("veriff.declined") },
    error: { icon: <AlertTriangle size={22} className="text-red-500" />, text: detail || t("veriff.error") },
  }[phase];

  return (
    <div data-testid="veriff-overlay" className="fixed inset-0 z-[60] bg-slate-900/60 backdrop-blur-sm grid place-items-center px-5">
      <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-7">
        <div className="flex items-start justify-between gap-3">
          <h3 className="font-heading text-lg font-semibold text-slate-900 flex items-center gap-2.5">
            <ShieldCheck size={19} className="text-blue-600" /> {t("veriff.title")}
          </h3>
          {phase !== "checking" && phase !== "approved" && (
            <button onClick={() => { stopPolling(); onClose?.(); }} data-testid="veriff-close" className="text-slate-400 hover:text-slate-600"><X size={18} /></button>
          )}
        </div>
        <div data-testid="veriff-status" className="flex items-start gap-3 mt-5 text-sm text-slate-600 leading-relaxed">
          {body.icon}
          <span className="flex-1">{body.text}</span>
        </div>
        {(phase === "declined" || phase === "error") && (
          <div className="flex gap-3 mt-6">
            <button onClick={start} data-testid="veriff-retry" className="flex-1 bg-blue-600 text-white py-2.5 rounded-full text-sm font-semibold hover:bg-blue-700 transition-colors">{t("veriff.tryAgain")}</button>
            <button onClick={() => { stopPolling(); onClose?.(); }} className="flex-1 border-2 border-slate-200 text-slate-600 py-2.5 rounded-full text-sm font-semibold hover:bg-slate-50">{t("common.close", "Close")}</button>
          </div>
        )}
        {phase === "review" && (
          <button onClick={() => { stopPolling(); onClose?.(); }} data-testid="veriff-review-close" className="mt-6 w-full border-2 border-slate-200 text-slate-600 py-2.5 rounded-full text-sm font-semibold hover:bg-slate-50">{t("common.close", "Close")}</button>
        )}
      </div>
    </div>
  );
}
